import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Autocomplete,
  Checkbox,
  FormControl,
  Grid,
  TextField,
} from "@mui/material";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";

export type GridSizeType = { xs?: number; sm?: number; md?: number; lg?: number };
const gridSizeDefault: GridSizeType = { xs: 12 };

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

export type FetchResult = {
  options: string[];
  hasMore: boolean;

};

export type CustomAutocompleteMultipleArrStringInfiniteProps = {
  /** Valor controlado (selecciones) */
  value?: string[];
  /** Valor inicial si no controlas desde afuera */
  defaultValue?: string[];

  /** Función para pedir página de resultados */
  fetchPage: (query: string, page: number) => Promise<FetchResult>;

  /** UI */
  label: string;
  placeholder?: string;
  loadingText?: string;
  disabled?: boolean;
  helperText?: React.ReactNode;
  required?: boolean;
  size?: GridSizeType;

  /** Comportamiento */
  pageSizeHint?: number;        // sólo referencial, por si quieres ajustar overscan
  debounceMs?: number;          // debounce de la búsqueda (default 350ms)
  initialQuery?: string;        // para precargar con un texto
  filterClientSide?: boolean;   // si tu API ya filtra, pon false (default: false)

  /** onChange: string[] o string JSON (si enableStringify=true) */
  onChangeValue?: (value: string[] | string) => void;
  enableStringify?: boolean;

  optionLabel?: (opt:string) => React.ReactNode;
};

export default function CustomAutocompleteMultipleArrDynamicSearch({
  value,
  defaultValue = [],
  fetchPage,
  optionLabel,
  label,
  placeholder = "Buscar…",
  loadingText = "Cargando...",
  helperText,
  required = false,
  disabled = false,
  size = gridSizeDefault,
  debounceMs = 350,
  initialQuery = "",
  filterClientSide = false,

  onChangeValue,
  enableStringify = false,
}: Readonly<CustomAutocompleteMultipleArrStringInfiniteProps>) {
  /** Estado de búsqueda y paginado */
  const [inputValue, setInputValue] = useState(initialQuery);
  const [page, setPage] = useState(1);
  const [options, setOptions] = useState<string[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);

  /** Evitar duplicados entre páginas */
  const allKeys = useRef<Set<string>>(new Set());

  /** Ref para loadMore - evita stale closures en onScroll */
  const loadMoreRef = useRef<() => Promise<void>>();

  /** Debounce manual simple */
  const debounceRef = useRef<number | undefined>(undefined);
  const clearDebounce = () => {
    if (debounceRef.current) {
      window.clearTimeout(debounceRef.current);
      debounceRef.current = undefined;
    }
  };

  /** Cargar página 1 ante cambios de query */
  useEffect(() => {
    clearDebounce();
    debounceRef.current = window.setTimeout(async () => {
      setLoading(true);
      try {
        const { options: firstOptions, hasMore } = await fetchPage(inputValue, 1);
        // Reset
        allKeys.current = new Set(firstOptions);
        setOptions(firstOptions);
        setPage(1);
        setHasMore(hasMore);
      } finally {
        setLoading(false);
      }
    }, debounceMs);

    return clearDebounce;
  }, [inputValue, fetchPage, debounceMs]);

  /** Cargar más (scroll) */
  const loadMore = useCallback(async () => {
    if (loading || !hasMore) return;
    setLoading(true);
    try {
      const next = page + 1;
      const { options: moreOptions, hasMore: more } = await fetchPage(inputValue, next);
      // dedupe
      const merged: string[] = [];
      for (const o of moreOptions) {
        if (!allKeys.current.has(o)) {
          allKeys.current.add(o);
          merged.push(o);
        }
      }
      setOptions((prev) => (merged.length ? [...prev, ...merged] : prev));
      setPage(next);
      setHasMore(more);
    } finally {
      setLoading(false);
    }
  }, [loading, hasMore, page, inputValue, fetchPage]);

  /** Mantener ref actualizado para evitar stale closures en onScroll */
  useEffect(() => {
    loadMoreRef.current = loadMore;
  }, [loadMore]);

  /** Opcional: filtro local (si tu API NO filtra). Si tu API ya filtra, déjalo en false */
  const shownOptions = useMemo(() => {
    if (!filterClientSide || !inputValue.trim()) return options;
    const q = inputValue.trim().toLowerCase();
    return options.filter((o) => o.toLowerCase().includes(q));
  }, [options, filterClientSide, inputValue]);

  /** Handler cambio de selección */
  const handleChange = useCallback(
    (_: any, data: string[]) => {
      if (!onChangeValue) return;
      if (!enableStringify) {
        onChangeValue(data);
      } else {
        onChangeValue(JSON.stringify(data ?? []));
      }
    },
    [onChangeValue, enableStringify]
  );

  return (
    <Grid item {...size}>
      <FormControl fullWidth>
        <Autocomplete
          multiple
          disableCloseOnSelect
          limitTags={3}
          options={shownOptions}
          value={value ?? undefined}
          defaultValue={value ? undefined : defaultValue}
          loading={loading}
          loadingText={loadingText}
          disabled={disabled}
          getOptionLabel={(option) => option}
          isOptionEqualToValue={(a, b) => a === b}
          onChange={handleChange}
          inputValue={inputValue}
          onInputChange={(_, v, reason) => {
            if (reason === "input") setInputValue(v ?? "");
            if (reason === "clear" || reason === "reset") setInputValue("");
          }}
          filterOptions={(opts) => opts} // evita doble filtro si ya filtras en servidor
          renderOption={(props, option, { selected }) => (
            <li {...props} 
              style={{
                whiteSpace: "nowrap",
                width: "max-content",
                minWidth: "100%",
              }}
            >
              <Checkbox
                icon={icon}
                checkedIcon={checkedIcon}
                style={{ marginRight: 8 }}
                checked={selected}
              />
              {optionLabel ? optionLabel(option) : option}
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label={label}
              placeholder={placeholder}
              variant="outlined"
              required={required}
              helperText={helperText}
            />
          )}
          ListboxProps={{
            onScroll: (e: React.UIEvent<HTMLElement>) => {
              const el = e.currentTarget;
              const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 8;
              if (nearBottom) loadMoreRef.current?.();
            },
            style: {
              maxHeight: 320,
              overflowY: "auto",
              overflowX: "auto",
              whiteSpace: "nowrap",
            },
          }}
        />
      </FormControl>
    </Grid>
  );
}
